import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';
import ImageUploadButton from '@/components/ImageUploadButton';

const EditProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [texts, setTexts] = useState({});  // Form texts from database
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    capacity_mw: 0,
    total_shares: 0,
    available_shares: 0,
    price_per_share: 0,
    image_url: '',
    images: [],
    expected_return: 0,
    duration_years: 0,
    payment_stages: [50, 30, 15, 5],  // Default payment stages
    payment_stage_descriptions: ['', '', '', ''],  // Descriptions for each payment stage
    project_progress: 0,  // Project completion progress
    badge_image_url: '',  // Badge/Button image URL
    badge_enabled: false,  // Enable/disable badge
    open_for_investments: false,  // Project open for investments
    status: 'active',
    is_visible: true,
    is_public: false,
    bank_details: {
      iban: '',
      bic: '',
      account_holder: '',
      bank_name: '',
      purpose: ''
    },
    site_plan_image: '',
    key_metrics: '',
    storage_technology: '',
    special_features: '',
    project_status_details: ''
  });
  const [sendingEmails, setSendingEmails] = useState(false);

  useEffect(() => {
    if (!user || !['admin', 'content_filler'].includes(user.role)) {
      navigate('/bess/dashboard');
      return;
    }
    fetchFormTexts();
    if (id !== 'new') {
      fetchProject();
    } else {
      setLoading(false);
    }
  }, [id, user]);

  const fetchFormTexts = async () => {
    try {
      const response = await axios.get(`${API}/settings/project-form-texts`);
      setTexts(response.data || {});
    } catch (error) {
      console.error('Error loading form texts:', error);
      // Use defaults if loading fails
      setTexts({});
    }
  };

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}`);
      setFormData({
        title: response.data.title,
        description: response.data.description,
        location: response.data.location,
        capacity_mw: response.data.capacity_mw,
        total_shares: response.data.total_shares,
        available_shares: response.data.available_shares,
        price_per_share: response.data.price_per_share,
        image_url: response.data.image_url,
        images: response.data.images || [],
        expected_return: response.data.expected_return,
        duration_years: response.data.duration_years,
        status: response.data.status,
        is_visible: response.data.is_visible !== undefined ? response.data.is_visible : true,
        is_public: response.data.is_public !== undefined ? response.data.is_public : false,
        bank_details: response.data.bank_details || {
          iban: '',
          bic: '',
          account_holder: '',
          bank_name: '',
          purpose: ''
        },
        site_plan_image: response.data.site_plan_image || '',
        key_metrics: response.data.key_metrics || '',
        storage_technology: response.data.storage_technology || '',
        special_features: response.data.special_features || '',
        project_status_details: response.data.project_status_details || '',
        payment_stages: response.data.payment_stages || [50, 30, 15, 5],
        payment_stage_descriptions: response.data.payment_stage_descriptions || ['', '', '', ''],
        project_progress: response.data.project_progress || 0,
        badge_image_url: response.data.badge_image_url || '',
        badge_enabled: response.data.badge_enabled || false,
        open_for_investments: response.data.open_for_investments || false
      });
    } catch (error) {
      toast.error('Fehler beim Laden des Projekts');
      navigate('/bess/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['capacity_mw', 'total_shares', 'available_shares', 'price_per_share', 'expected_return', 'duration_years'].includes(name)
        ? parseFloat(value) || 0
        : value
    }));
  };

  const handlePaymentStageChange = (index, value) => {
    const newStages = [...formData.payment_stages];
    newStages[index] = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, payment_stages: newStages }));
  };

  const handlePaymentStageDescriptionChange = (index, value) => {
    const newDescriptions = [...formData.payment_stage_descriptions];
    newDescriptions[index] = value;
    setFormData(prev => ({ ...prev, payment_stage_descriptions: newDescriptions }));
  };

  const handleSendPaymentReminders = async () => {
    if (!window.confirm('Möchten Sie wirklich Zahlungserinnerungen an alle betroffenen Investoren senden?')) {
      return;
    }

    setSendingEmails(true);
    try {
      const response = await axios.post(`${API}/projects/${id}/send-payment-reminders`);
      toast.success(response.data.message || 'Emails erfolgreich versendet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Versenden der Emails');
    } finally {
      setSendingEmails(false);
    }
  };

  const handleBankDetailsChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      bank_details: {
        ...prev.bank_details,
        [name]: value
      }
    }));
  };

  const handleImagesChange = (e) => {
    const imagesText = e.target.value;
    const imagesArray = imagesText.split('\n').filter(url => url.trim());
    setFormData(prev => ({ ...prev, images: imagesArray }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (id === 'new') {
        await axios.post(`${API}/projects`, formData);
        toast.success('Projekt erfolgreich erstellt!');
      } else {
        await axios.put(`${API}/projects/${id}`, formData);
        toast.success('Projekt erfolgreich aktualisiert!');
      }
      navigate('/bess/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-lg text-slate-600">Projekt wird geladen...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12" data-testid="edit-project-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/bess/dashboard')} className="mb-4">
            <ArrowLeft size={20} className="mr-2" />
            {texts.button_back || 'Zurück zum Dashboard'}
          </Button>
          <h1 className="text-4xl font-bold text-slate-900">
            {id === 'new' ? 'Neues Projekt erstellen' : 'Projekt bearbeiten'}
          </h1>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="title">{texts.label_title || 'Projekttitel'} *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="z.B. BESS Berlin Kraftwerk"
                  data-testid="title-input"
                />
                {formData.title && (
                  <p className="text-xs text-slate-500 mt-1">
                    <strong>URL-Slug:</strong> {formData.title.toLowerCase()
                      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
                      .replace(/[^a-z0-9\s-]/g, '')
                      .replace(/[\s-]+/g, '-')
                      .replace(/^-+|-+$/g, '')}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="location">{texts.label_location || 'Standort'} *</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  placeholder="z.B. Berlin, Deutschland"
                  data-testid="location-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Beschreibung *</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={6}
                placeholder="Detaillierte Projektbeschreibung..."
                data-testid="description-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label htmlFor="capacity_mw">Kapazität (MWh) *</Label>
                <Input
                  id="capacity_mw"
                  name="capacity_mw"
                  type="number"
                  step="0.1"
                  value={formData.capacity_mw}
                  onChange={handleChange}
                  required
                  data-testid="capacity-input"
                />
              </div>

              <div>
                <Label htmlFor="expected_return">Rendite p.a. (%) *</Label>
                <Input
                  id="expected_return"
                  name="expected_return"
                  type="number"
                  step="0.1"
                  value={formData.expected_return}
                  onChange={handleChange}
                  required
                  data-testid="return-input"
                />
              </div>

              <div>
                <Label htmlFor="duration_years">Laufzeit (Jahre) *</Label>
                <Input
                  id="duration_years"
                  name="duration_years"
                  type="number"
                  value={formData.duration_years}
                  onChange={handleChange}
                  required
                  data-testid="duration-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="total_shares">Gesamte Anteile *</Label>
                <Input
                  id="total_shares"
                  name="total_shares"
                  type="number"
                  value={formData.total_shares}
                  onChange={handleChange}
                  required
                  data-testid="shares-input"
                />
              </div>

              <div>
                <Label htmlFor="price_per_share">Preis pro Anteil (€) *</Label>
                <Input
                  id="price_per_share"
                  name="price_per_share"
                  type="number"
                  step="0.01"
                  value={formData.price_per_share}
                  onChange={handleChange}
                  required
                  data-testid="price-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {user && user.role === 'admin' && (
                <div>
                  <Label htmlFor="available_shares">Verfügbare Anteile (nur Admin) *</Label>
                  <Input
                    id="available_shares"
                    name="available_shares"
                    type="number"
                    value={formData.available_shares}
                    onChange={handleChange}
                    required
                    data-testid="available-shares-input"
                    className="border-yellow-400"
                  />
                  <p className="text-xs text-yellow-600 mt-1">
                    ⚠️ Achtung: Änderungen überschreiben automatische Berechnung durch Investitionen!
                  </p>
                </div>
              )}
              
              <div>
                <Label htmlFor="status">Status *</Label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border-2 border-slate-200 rounded-md focus:outline-none focus:border-green-500"
                  data-testid="status-select"
                >
                  <option value="active">Aktiv</option>
                  <option value="upcoming">Bevorstehend</option>
                  <option value="completed">Abgeschlossen</option>
                </select>
              </div>
            </div>

            {/* Payment Stages Section */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Zahlungsstufen (4-Stufen-Plan)</h3>
              <p className="text-sm text-slate-600 mb-4">
                Definieren Sie die Prozentsätze für die 4 Zahlungsstufen. Die Summe sollte 100% ergeben.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="stage1">Stufe 1 (%)</Label>
                  <Input
                    id="stage1"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.payment_stages[0]}
                    onChange={(e) => handlePaymentStageChange(0, e.target.value)}
                    className="border-blue-300"
                  />
                  <Label htmlFor="stage1_desc" className="text-xs text-slate-500 mt-2">Beschreibung (optional)</Label>
                  <Input
                    id="stage1_desc"
                    type="text"
                    placeholder="z.B. Anzahlung bei Vertragsabschluss"
                    value={formData.payment_stage_descriptions[0]}
                    onChange={(e) => handlePaymentStageDescriptionChange(0, e.target.value)}
                    className="mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="stage2">Stufe 2 (%)</Label>
                  <Input
                    id="stage2"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.payment_stages[1]}
                    onChange={(e) => handlePaymentStageChange(1, e.target.value)}
                    className="border-blue-300"
                  />
                  <Label htmlFor="stage2_desc" className="text-xs text-slate-500 mt-2">Beschreibung (optional)</Label>
                  <Input
                    id="stage2_desc"
                    type="text"
                    placeholder="z.B. Nach Baufortschritt 25%"
                    value={formData.payment_stage_descriptions[1]}
                    onChange={(e) => handlePaymentStageDescriptionChange(1, e.target.value)}
                    className="mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="stage3">Stufe 3 (%)</Label>
                  <Input
                    id="stage3"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.payment_stages[2]}
                    onChange={(e) => handlePaymentStageChange(2, e.target.value)}
                    className="border-blue-300"
                  />
                  <Label htmlFor="stage3_desc" className="text-xs text-slate-500 mt-2">Beschreibung (optional)</Label>
                  <Input
                    id="stage3_desc"
                    type="text"
                    placeholder="z.B. Nach Baufortschritt 50%"
                    value={formData.payment_stage_descriptions[2]}
                    onChange={(e) => handlePaymentStageDescriptionChange(2, e.target.value)}
                    className="mt-1 text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="stage4">Stufe 4 (%)</Label>
                  <Input
                    id="stage4"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.payment_stages[3]}
                    onChange={(e) => handlePaymentStageChange(3, e.target.value)}
                    className="border-blue-300"
                  />
                  <Label htmlFor="stage4_desc" className="text-xs text-slate-500 mt-2">Beschreibung (optional)</Label>
                  <Input
                    id="stage4_desc"
                    type="text"
                    placeholder="z.B. Bei Projektabschluss"
                    value={formData.payment_stage_descriptions[3]}
                    onChange={(e) => handlePaymentStageDescriptionChange(3, e.target.value)}
                    className="mt-1 text-sm"
                  />
                </div>
              </div>
              <div className="mt-2">
                <p className="text-sm text-slate-600">
                  Summe: <span className={`font-bold ${formData.payment_stages.reduce((a, b) => a + b, 0) === 100 ? 'text-green-600' : 'text-red-600'}`}>
                    {formData.payment_stages.reduce((a, b) => a + b, 0).toFixed(1)}%
                  </span>
                  {formData.payment_stages.reduce((a, b) => a + b, 0) !== 100 && (
                    <span className="text-red-600 ml-2">⚠️ Summe sollte 100% sein</span>
                  )}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Kumulativ: {formData.payment_stages[0]}% → {formData.payment_stages[0] + formData.payment_stages[1]}% → {formData.payment_stages[0] + formData.payment_stages[1] + formData.payment_stages[2]}% → 100%
                </p>
              </div>
            </div>

            {/* Project Progress Section */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Projektfortschritt</h3>
              <p className="text-sm text-slate-600 mb-4">
                Geben Sie den aktuellen Projektfortschritt an. Wenn dieser eine Zahlungsstufe erreicht, können Sie Zahlungserinnerungen an Investoren senden.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="project_progress">Projektfortschritt (%)</Label>
                  <Input
                    id="project_progress"
                    name="project_progress"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.project_progress}
                    onChange={handleChange}
                    className="border-green-300"
                  />
                  <p className="text-xs text-slate-500 mt-1">Aktueller Baufortschritt des Projekts</p>
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    onClick={handleSendPaymentReminders}
                    disabled={sendingEmails || formData.project_progress === 0}
                    className="bg-orange-600 hover:bg-orange-700 w-full"
                  >
                    {sendingEmails ? 'Sende Emails...' : '📧 Zahlungserinnerungen senden'}
                  </Button>
                </div>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded border border-blue-200">
                <p className="text-sm text-blue-900">
                  <strong>Hinweis:</strong> Emails werden nur an Investoren gesendet, deren Zahlungsfortschritt hinter dem Projektfortschritt liegt. 
                  Jeder Investor erhält nur einmal pro Stufe eine Email.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Visibility for logged in investors */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-lg font-semibold text-slate-900">Sichtbarkeit für Investoren</Label>
                    <p className="text-sm text-slate-600 mt-1">
                      {formData.is_visible 
                        ? '✅ Projekt ist für eingeloggte Investoren sichtbar' 
                        : '🔒 Projekt ist für Investoren ausgeblendet (nur für Admins sichtbar)'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_visible}
                      onChange={(e) => setFormData({...formData, is_visible: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
              </div>

              {/* Public visibility for non-logged users */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-lg font-semibold text-slate-900">Öffentlich Sichtbar</Label>
                    <p className="text-sm text-slate-600 mt-1">
                      {formData.is_public 
                        ? '✅ Projekt ist für alle Besucher (auch ohne Login) sichtbar' 
                        : '🔒 Projekt ist nur für eingeloggte Benutzer sichtbar'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_public}
                      onChange={(e) => setFormData({...formData, is_public: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Investment Status Toggle */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 mt-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">💰 Investment-Status</h3>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-semibold text-slate-900">Offen für Investments</Label>
                  <p className="text-sm text-slate-600 mt-1">
                    {formData.open_for_investments 
                      ? '🟢 Projekt ist offen für Investitionen und Interessenbekundungen' 
                      : '🔴 Projekt ist geschlossen für Investments'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.open_for_investments}
                    onChange={(e) => setFormData({...formData, open_for_investments: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
            </div>

            {/* Project Badge Section */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-lg p-6 mt-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">🏷️ Projekt-Badge (Übersichtsbild)</h3>
              <p className="text-sm text-slate-600 mb-4">
                Fügen Sie ein Badge-Bild hinzu, das oben rechts auf dem Projektbild in der Übersicht angezeigt wird (z.B. "NEU", "BELIEBT", "LIMITIERT").
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="badge_image_url">Badge-Bild URL</Label>
                  <Input
                    id="badge_image_url"
                    type="text"
                    placeholder="https://example.com/badge-neu.png"
                    value={formData.badge_image_url}
                    onChange={(e) => setFormData({...formData, badge_image_url: e.target.value})}
                    className="mt-1"
                  />
                  <ImageUploadButton
                    testId="badge-upload"
                    onUploaded={(url) => setFormData(prev => ({ ...prev, badge_image_url: url }))}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Empfohlene Größe: 80x80px oder 100x100px (PNG mit transparentem Hintergrund)
                  </p>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold text-slate-900">Badge aktivieren</Label>
                    <p className="text-sm text-slate-600 mt-1">
                      {formData.badge_enabled 
                        ? '✅ Badge wird auf Projektbild angezeigt' 
                        : '⭕ Badge ist deaktiviert'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.badge_enabled}
                      onChange={(e) => setFormData({...formData, badge_enabled: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-600"></div>
                  </label>
                </div>
              </div>
              
              {/* Preview */}
              {formData.badge_image_url && formData.badge_enabled && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-orange-200">
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Vorschau:</Label>
                  <div className="relative inline-block">
                    <div className="w-64 h-40 bg-slate-200 rounded-lg flex items-center justify-center text-slate-500">
                      Projekt-Bild
                    </div>
                    <img 
                      src={formData.badge_image_url} 
                      alt="Badge Preview" 
                      className="absolute top-2 right-2 w-16 h-16 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {user && user.role !== 'admin' && id !== 'new' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Info:</strong> Verfügbare Anteile werden automatisch durch User-Investitionen aktualisiert.<br />
                  Aktuell verfügbar: <strong>{formData.available_shares}</strong> von {formData.total_shares} Anteilen
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="image_url">Hauptbild URL *</Label>
              <Input
                id="image_url"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                required
                placeholder="Bild hochladen oder URL einfügen"
                data-testid="image-url-input"
              />
              <ImageUploadButton
                testId="image-url-upload"
                onUploaded={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
              />
              <p className="text-sm text-slate-500 mt-1">
                💡 <strong>Tipp:</strong> Bild direkt hochladen, es wird auf dem eigenen Server gespeichert. Links zu fremden Diensten (z. B. Google Drive) vermeiden, sie verbinden Besucher mit Dritten.
              </p>
              {formData.image_url && (
                <img src={formData.image_url} alt="Vorschau" className="mt-2 w-full h-48 object-cover rounded-lg" />
              )}
            </div>

            <div>
              <Label htmlFor="images">Zusätzliche Bilder (eine URL pro Zeile)</Label>
              <Textarea
                id="images"
                value={formData.images.join('\n')}
                onChange={handleImagesChange}
                rows={4}
                placeholder="https://image1.jpg&#10;https://image2.jpg&#10;https://drive.google.com/file/d/..."
                data-testid="images-input"
              />
              <ImageUploadButton
                label="Bild hinzufügen (hochladen)"
                testId="images-upload"
                onUploaded={(url) => setFormData(prev => ({ ...prev, images: [...prev.images, url] }))}
              />
              <p className="text-sm text-slate-500 mt-1">
                Eine Bild-URL pro Zeile. Hochgeladene Bilder werden automatisch als neue Zeile angehängt.
              </p>
            </div>

            {/* Bank Details Section - Only for Admin */}
            {user && user.role === 'admin' && (
              <div className="border-t pt-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">🏦 Bankverbindung</h3>
                  <p className="text-sm text-slate-600">
                    Diese Bankdaten werden für Überweisungen der Investoren verwendet.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="iban">IBAN</Label>
                    <Input
                      id="iban"
                      name="iban"
                      value={formData.bank_details.iban}
                      onChange={handleBankDetailsChange}
                      placeholder="DE89 3704 0044 0532 0130 00"
                      data-testid="iban-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="bic">BIC</Label>
                    <Input
                      id="bic"
                      name="bic"
                      value={formData.bank_details.bic}
                      onChange={handleBankDetailsChange}
                      placeholder="COBADEFFXXX"
                      data-testid="bic-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="account_holder">Kontoinhaber</Label>
                    <Input
                      id="account_holder"
                      name="account_holder"
                      value={formData.bank_details.account_holder}
                      onChange={handleBankDetailsChange}
                      placeholder="Speier Solarbetriebe"
                      data-testid="account-holder-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="bank_name">Bank Name</Label>
                    <Input
                      id="bank_name"
                      name="bank_name"
                      value={formData.bank_details.bank_name}
                      onChange={handleBankDetailsChange}
                      placeholder="Commerzbank AG"
                      data-testid="bank-name-input"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="purpose">Verwendungszweck (optional)</Label>
                    <Input
                      id="purpose"
                      name="purpose"
                      value={formData.bank_details.purpose || ''}
                      onChange={handleBankDetailsChange}
                      placeholder="z.B. Investition {Projektname} - {Investitionsnummer}"
                      data-testid="purpose-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Wird Investoren bei der Überweisung angezeigt
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Investor-Only Detailed Information - Available for Admin and Content Filler */}
            {user && ['admin', 'content_filler'].includes(user.role) && (
              <div className="border-t pt-6 mt-6">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">🔒 Detailinformationen für Investoren</h3>
                  <p className="text-sm text-slate-600">
                    Diese Informationen sind nur für freigeschaltete Investoren sichtbar.
                  </p>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="site_plan_image">1. Grundstücksplan (Bild-URL)</Label>
                    <Input
                      id="site_plan_image"
                      name="site_plan_image"
                      value={formData.site_plan_image}
                      onChange={handleChange}
                      placeholder="https://... oder Google Drive Link"
                      data-testid="site-plan-input"
                    />
                    <ImageUploadButton
                      testId="site-plan-upload"
                      onUploaded={(url) => setFormData(prev => ({ ...prev, site_plan_image: url }))}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Grundstücksplan hochladen oder Bild-URL einfügen
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="key_metrics">2. Kennzahlen des Projekts</Label>
                    <Textarea
                      id="key_metrics"
                      name="key_metrics"
                      value={formData.key_metrics}
                      onChange={handleChange}
                      rows={6}
                      placeholder="z.B. Nennleistung, Speicherkapazität, Effizienz, etc."
                      data-testid="key-metrics-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="storage_technology">3. Eingesetzte Speichertechnologie</Label>
                    <Textarea
                      id="storage_technology"
                      name="storage_technology"
                      value={formData.storage_technology}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Beschreibung der verwendeten Batterietechnologie..."
                      data-testid="storage-technology-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="special_features">4. Besonderheiten</Label>
                    <Textarea
                      id="special_features"
                      name="special_features"
                      value={formData.special_features}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Besondere Merkmale oder Highlights des Projekts..."
                      data-testid="special-features-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="project_status_details">5. Detaillierter Projektstatus</Label>
                    <Textarea
                      id="project_status_details"
                      name="project_status_details"
                      value={formData.project_status_details}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Aktueller Status, Baufortschritt, Meilensteine..."
                      data-testid="project-status-details-input"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/bess/dashboard')}
                data-testid="cancel-btn"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white"
                data-testid="save-btn"
              >
                {saving ? 'Wird gespeichert...' : (
                  <>
                    <Save size={20} className="mr-2" />
                    Speichern
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProject;
